(function () {
  const config = window.SquishTheme || {};
  const selectors = {
    cartDrawer: '[data-cart-drawer]',
    cartItems: '[data-cart-items]',
    cartCount: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    mobileMenu: '[data-mobile-menu]',
    exitPopup: '[data-exit-popup]',
    wishlistDrawer: '[data-wishlist-drawer]'
  };

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: config.currency || 'USD'
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function lockBody(locked) {
    document.body.classList.toggle('is-locked', locked);
  }

  function openLayer(layer) {
    if (!layer) return;
    layer.classList.add('is-open');
    layer.setAttribute('aria-hidden', 'false');
    lockBody(true);
    const panel = qs('[role="dialog"], [tabindex="-1"]', layer);
    if (panel) panel.focus({ preventScroll: true });
  }

  function closeLayer(layer) {
    if (!layer) return;
    layer.classList.remove('is-open');
    layer.setAttribute('aria-hidden', 'true');
    if (!qs('.cart-drawer.is-open, .mobile-menu.is-open, .discount-popup.is-open, .wishlist-drawer.is-open')) {
      lockBody(false);
    }
  }

  function updateCartCount(count) {
    qsa(selectors.cartCount).forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  function updateFreeShipping(cart) {
    const threshold = Number(config.freeShippingThreshold || 0);
    qsa('[data-free-shipping]').forEach((node) => {
      const copy = qs('[data-free-shipping-copy]', node);
      const bar = qs('[data-free-shipping-bar]', node);
      if (!threshold) {
        if (copy) copy.textContent = 'Shipping options are shown at checkout.';
        if (bar) bar.style.width = '0%';
        return;
      }

      const remaining = Math.max(threshold - cart.total_price, 0);
      const percent = Math.min((cart.total_price / threshold) * 100, 100);
      if (copy) {
        copy.innerHTML = remaining > 0
          ? `Add <strong>${formatMoney(remaining)}</strong> more to unlock the shipping perk.`
          : 'You unlocked the shipping perk.';
      }
      if (bar) bar.style.width = `${percent}%`;
    });
  }

  function variantText(item) {
    if (!item.options_with_values || item.options_with_values.length === 0) return '';
    return item.options_with_values
      .filter((option) => option.value && option.value !== 'Default Title')
      .map((option) => `${escapeHtml(option.name)}: ${escapeHtml(option.value)}`)
      .join(', ');
  }

  function cartItemImage(item) {
    if (item.featured_image && item.featured_image.url) return item.featured_image.url;
    if (item.image) return item.image;
    return '';
  }

  function renderCart(cart) {
    updateCartCount(cart.item_count);
    updateFreeShipping(cart);

    qsa('[data-cart-has-items]').forEach((node) => {
      const isEmpty = cart.item_count === 0;
      node.hidden = isEmpty;
      node.style.display = isEmpty ? 'none' : '';
    });

    const cartHandles = cart.items.map((item) => item.handle);
    qsa('[data-cart-upsell]').forEach((node) => {
      const handle = node.getAttribute('data-upsell-handle');
      node.hidden = cart.item_count === 0 || cartHandles.includes(handle);
    });

    qsa(selectors.cartSubtotal).forEach((node) => {
      node.textContent = formatMoney(cart.total_price);
    });

    const container = qs(selectors.cartItems);
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = `
        <div class="cart-empty">
          <svg class="cart-empty__icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M6.75 8.25h10.5l.75 11.25H6L6.75 8.25Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 8.25V6a3 3 0 0 1 6 0v2.25" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          <p>Your cart is empty</p>
          <a class="button button--secondary" href="/collections/all">Continue shopping</a>
        </div>
      `;
      return;
    }

    container.innerHTML = cart.items.map((item) => {
      const image = cartItemImage(item);
      const options = variantText(item);
      return `
        <article class="cart-line" data-line-key="${escapeHtml(item.key)}">
          <a class="cart-line__image" href="${escapeHtml(item.url)}">
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">` : ''}
          </a>
          <div class="cart-line__content">
            <a class="cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${options ? `<p class="cart-line__variant">${options}</p>` : ''}
            <div class="cart-line__bottom">
              <div class="quantity-stepper">
                <button type="button" data-cart-quantity="${Math.max(item.quantity - 1, 0)}" data-line-key="${escapeHtml(item.key)}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity">-</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-quantity="${item.quantity + 1}" data-line-key="${escapeHtml(item.key)}" aria-label="Increase ${escapeHtml(item.product_title)} quantity">+</button>
              </div>
              <span class="cart-line__price">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </article>
      `;
    }).join('');
  }

  async function fetchCart() {
    const response = await fetch(`${config.routes?.cart || '/cart'}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Could not load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  async function addToCart(form) {
    const button = qs('[type="submit"]', form);
    const previousText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }

    try {
      const response = await fetch(config.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Could not add item');
      await fetchCart();
      openLayer(qs(selectors.cartDrawer));
    } catch (error) {
      window.location.href = config.routes?.cart || '/cart';
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = previousText;
      }
    }
  }

  async function changeCartQuantity(key, quantity) {
    const response = await fetch(config.routes?.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Could not update cart');
    const cart = await response.json();
    renderCart(cart);
  }

  function setupMenu() {
    const menu = qs(selectors.mobileMenu);
    qsa('[data-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        button.setAttribute('aria-expanded', 'true');
        openLayer(menu);
      });
    });
    qsa('[data-menu-close]').forEach((button) => {
      button.addEventListener('click', () => {
        qsa('[data-menu-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
        closeLayer(menu);
      });
    });
  }

  function setupCart() {
    qsa('[data-cart-toggle]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (!config.enableAjaxCart) {
          window.location.href = config.routes?.cart || '/cart';
          return;
        }
        openLayer(qs(selectors.cartDrawer));
        try {
          await fetchCart();
        } catch (error) {
          window.location.href = config.routes?.cart || '/cart';
        }
      });
    });

    qsa('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => closeLayer(qs(selectors.cartDrawer)));
    });

    document.addEventListener('click', async (event) => {
      const quantityButton = event.target.closest('[data-cart-quantity]');
      if (!quantityButton) return;
      const key = quantityButton.getAttribute('data-line-key');
      const quantity = Number(quantityButton.getAttribute('data-cart-quantity'));
      quantityButton.disabled = true;
      try {
        await changeCartQuantity(key, quantity);
      } finally {
        quantityButton.disabled = false;
      }
    });

    qsa('form[data-product-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!config.enableAjaxCart) return;
        if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
        event.preventDefault();
        addToCart(form);
      });
    });
  }

  function findVariant(wrapper) {
    const script = qs('[data-variant-json]', wrapper);
    if (!script) return null;
    const variants = JSON.parse(script.textContent);
    const selectedOptions = qsa('input:checked', wrapper).map((input) => input.value);
    return variants.find((variant) => {
      return selectedOptions.every((option, index) => variant.options[index] === option);
    });
  }

  function setupVariants() {
    qsa('[data-product-section]').forEach((section) => {
      const wrapper = qs('[data-product-variants]', section);
      const form = qs('form[data-product-form]', section);
      const variantInput = qs('[data-variant-id]', section);
      const submit = qs('[data-product-submit]', section);
      const price = qs('[data-price] .price__current', section);
      const stockMessage = qs('[data-stock-message]', section);
      const stickyPrice = qs('[data-sticky-price]');
      const stickyButton = qs('[data-sticky-add]');
      if (!wrapper || !form || !variantInput) return;

      const update = () => {
        const variant = findVariant(wrapper);
        if (!variant) return;
        variantInput.value = variant.id;
        if (price) price.textContent = formatMoney(variant.price);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }
        if (stickyButton) {
          stickyButton.disabled = !variant.available;
          stickyButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }
        if (stockMessage) {
          const tracked = Boolean(variant.inventory_management);
          const lowStock = tracked && variant.inventory_quantity > 0 && variant.inventory_quantity < 8;
          stockMessage.hidden = !lowStock;
          stockMessage.textContent = lowStock ? `${variant.inventory_quantity} available` : '';
        }
      };

      wrapper.addEventListener('change', update);
      update();
    });
  }

  function setupQuantitySteppers() {
    document.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      if (!minus && !plus) return;
      const stepper = event.target.closest('.quantity-stepper');
      const input = qs('input[type="number"]', stepper);
      if (!input) return;
      const current = Number(input.value || 1);
      input.value = Math.max(1, current + (plus ? 1 : -1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setupStickyAddBar() {
    const bar = qs('[data-sticky-add-bar]');
    const form = qs('[data-product-section] form[data-product-form]');
    if (!bar || !form) return;
    const submit = qs('[data-product-submit]', form);
    const observer = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0 });
    observer.observe(submit || form);
    qs('[data-sticky-add]', bar)?.addEventListener('click', () => {
      if (submit) submit.click();
    });
  }

  function setupWishlist() {
    if (!config.enableWishlist) return;
    const key = 'squishpop:wishlist';
    const drawer = qs(selectors.wishlistDrawer);
    const read = () => {
      try {
        return JSON.parse(localStorage.getItem(key) || '[]').filter((item) => item && typeof item === 'object');
      } catch (error) {
        return [];
      }
    };
    const write = (items) => localStorage.setItem(key, JSON.stringify(items));

    const itemFromButton = (button) => ({
      handle: button.getAttribute('data-wishlist-handle'),
      title: button.getAttribute('data-wishlist-title'),
      url: button.getAttribute('data-wishlist-url'),
      image: button.getAttribute('data-wishlist-image'),
      price: Number(button.getAttribute('data-wishlist-price') || 0)
    });

    const render = () => {
      const items = read();
      qsa('[data-wishlist-count]').forEach((node) => {
        node.textContent = items.length;
        node.hidden = items.length === 0 && !node.closest('.mobile-menu__links');
      });

      const container = qs('[data-wishlist-items]');
      if (!container) return;
      if (!items.length) {
        container.innerHTML = `
          <div class="wishlist-empty">
            <svg class="cart-empty__icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M12 20s-7.5-4.35-7.5-10.2A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 7.5 2.8C19.5 15.65 12 20 12 20Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>
            <strong>No saved squishies yet</strong>
            <p>Tap the heart on any product to keep it here.</p>
          </div>`;
        return;
      }

      container.innerHTML = items.map((item) => `
        <article class="wishlist-line">
          <a class="wishlist-line__image" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="80" height="80" loading="lazy">` : ''}
          </a>
          <div>
            <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.title)}</strong></a>
            <span>${formatMoney(item.price)}</span>
            <button type="button" data-wishlist-remove="${escapeHtml(item.handle)}">Remove</button>
          </div>
        </article>
      `).join('');
    };

    const refresh = () => {
      const items = read();
      qsa('[data-wishlist-toggle]').forEach((button) => {
        const handle = button.getAttribute('data-wishlist-handle');
        const active = items.some((item) => item.handle === handle);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        const label = qs('span', button);
        if (label) label.textContent = active ? 'Saved for later' : 'Save for later';
      });
      render();
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (button) {
        const item = itemFromButton(button);
        const items = read();
        const exists = items.some((saved) => saved.handle === item.handle);
        write(exists ? items.filter((saved) => saved.handle !== item.handle) : [item, ...items]);
        refresh();
        return;
      }

      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        write(read().filter((item) => item.handle !== remove.getAttribute('data-wishlist-remove')));
        refresh();
      }
    });

    qsa('[data-wishlist-open]').forEach((button) => {
      button.addEventListener('click', () => {
        closeLayer(qs(selectors.mobileMenu));
        openLayer(drawer);
      });
    });
    qsa('[data-wishlist-close]').forEach((button) => {
      button.addEventListener('click', () => closeLayer(drawer));
    });

    refresh();
  }

  function setupExitPopup() {
    if (!config.enableExitPopup || sessionStorage.getItem('squishpop:exit-popup')) return;
    const popup = qs(selectors.exitPopup);
    if (!popup) return;
    const show = () => {
      sessionStorage.setItem('squishpop:exit-popup', 'shown');
      openLayer(popup);
    };

    document.addEventListener('mouseleave', (event) => {
      if (event.clientY <= 0 && !sessionStorage.getItem('squishpop:exit-popup')) show();
    });
    window.setTimeout(() => {
      if (window.matchMedia('(max-width: 749px)').matches && !sessionStorage.getItem('squishpop:exit-popup')) {
        show();
      }
    }, 22000);
    qsa('[data-exit-close]').forEach((button) => {
      button.addEventListener('click', () => closeLayer(popup));
    });
  }

  function setupMysteryCards() {
    document.addEventListener('click', (event) => {
      const card = event.target.closest('[data-mystery-card]');
      if (!card) return;
      qsa('[data-mystery-card]', card.closest('[data-mystery-game]')).forEach((other) => {
        if (other !== card) other.classList.remove('is-revealed');
      });
      card.classList.toggle('is-revealed');
    });
  }

  function setupRecentlyViewed() {
    const key = 'squishpop:recently-viewed';
    const productHandle = document.body.dataset.productHandle;
    const read = () => JSON.parse(localStorage.getItem(key) || '[]');
    const write = (items) => localStorage.setItem(key, JSON.stringify(items.slice(0, 8)));

    if (productHandle) {
      const product = {
        handle: productHandle,
        title: document.body.dataset.productTitle,
        url: document.body.dataset.productUrl,
        image: document.body.dataset.productImage,
        price: Number(document.body.dataset.productPrice || 0)
      };
      write([product, ...read().filter((item) => item.handle !== productHandle)]);
    }

    const section = qs('[data-recently-viewed]');
    const grid = qs('[data-recently-viewed-grid]');
    if (!section || !grid) return;
    const items = read().filter((item) => item.handle !== productHandle).slice(0, 4);
    if (!items.length) return;
    grid.innerHTML = items.map((item) => `
      <a class="recent-card" href="${escapeHtml(item.url)}">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
        <strong>${escapeHtml(item.title)}</strong>
        <span>${formatMoney(item.price)}</span>
      </a>
    `).join('');
    section.hidden = false;
  }

  function setupPurchasePop() {
    const pop = qs('[data-purchase-pop]');
    if (!pop) return;
    const messages = [
      ['Viral favorites are getting attention.', 'Explore colorful squishies, fidgets, and gifts'],
      ['Build a squishy box your way.', 'Mix mystery picks, desk fidgets, and giftable favorites'],
      ['Mystery packs are made for surprise hauls.', 'Open a box and see what squeeze shows up']
    ];
    let index = 0;
    window.setInterval(() => {
      const message = messages[index % messages.length];
      qs('[data-purchase-pop-title]', pop).textContent = message[0];
      qs('[data-purchase-pop-meta]', pop).textContent = message[1];
      pop.hidden = false;
      window.setTimeout(() => { pop.hidden = true; }, 7000);
      index += 1;
    }, 22000);
  }

  function setupChaosHome() {
    const meter = qs('[data-squish-meter]');
    if (!meter) return;
    const lines = [
      'Fresh squishy picks are ready',
      'Mystery-box surprises are waiting',
      'Desk fidget favorites just landed',
      'Your next desk squeeze is waiting',
      'Rare drop radar is glowing'
    ];
    let index = 0;
    window.setInterval(() => {
      index = (index + 1) % lines.length;
      meter.textContent = lines[index];
    }, 2600);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeLayer(qs(selectors.mobileMenu));
    closeLayer(qs(selectors.cartDrawer));
    closeLayer(qs(selectors.exitPopup));
    closeLayer(qs(selectors.wishlistDrawer));
  });

  document.addEventListener('DOMContentLoaded', () => {
    setupMenu();
    setupCart();
    setupVariants();
    setupQuantitySteppers();
    setupStickyAddBar();
    setupWishlist();
    setupExitPopup();
    setupMysteryCards();
    setupRecentlyViewed();
    setupPurchasePop();
    setupChaosHome();
  });
})();
